import React, { useState, useRef } from 'react';
import * as LucideIcons from 'lucide-react';
import { uploadClient, endpoints } from '../../api';
import './KYCUpload.css';

const KYCUpload = ({ onKYCComplete, onSkip, vendorId }) => {
  const [documents, setDocuments] = useState({
    fssai: null,
    shopLicense: null,
    pan: null,
    aadhaar: null,
    bankProof: null
  });
  
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState({});
  const [aadhaarMaskedConfirmed, setAadhaarMaskedConfirmed] = useState(false);
  const fileInputRefs = useRef({});

  const documentTypes = {
    fssai: { 
      label: 'FSSAI License', 
      required: false, 
      maxSize: 5,
      description: 'Upload your FSSAI license document',
      acceptedFormats: 'JPG, PNG, PDF'
    },
    shopLicense: {
      label: 'Shop/Trade License',
      required: true,
      maxSize: 5,
      description: 'Upload your valid Shop/Trade license',
      acceptedFormats: 'JPG, PNG, PDF'
    },
    pan: { 
      label: 'PAN Card', 
      required: true, 
      maxSize: 2,
      description: 'Upload your PAN card (front and back)',
      acceptedFormats: 'JPG, PNG, PDF'
    },
    aadhaar: { 
      label: 'Aadhaar Card', 
      required: true, 
      maxSize: 2,
      description: 'Upload your masked Aadhaar (first 8 digits hidden) - front/back',
      acceptedFormats: 'JPG, PNG, PDF'
    },
    bankProof: { 
      label: 'Bank Account Proof', 
      required: true, 
      maxSize: 5,
      description: 'Upload bank statement or cancelled cheque',
      acceptedFormats: 'JPG, PNG, PDF'
    }
  };

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
  const maxFileSize = 5 * 1024 * 1024; // 5MB

  const handleFileSelect = (docType, file) => {
    const newErrors = { ...errors };
    delete newErrors[docType];
    setErrors(newErrors);

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({
        ...prev,
        [docType]: `Only ${documentTypes[docType].acceptedFormats} files are allowed`
      }));
      return;
    }

    // Validate file size
    const maxSizeBytes = documentTypes[docType].maxSize * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setErrors(prev => ({
        ...prev,
        [docType]: `File size must be less than ${documentTypes[docType].maxSize}MB`
      }));
      return;
    }

    // Aadhaar masking confirmation
    if (docType === 'aadhaar' && !aadhaarMaskedConfirmed) {
      setErrors(prev => ({
        ...prev,
        aadhaar: 'Please ensure Aadhaar is masked (first 8 digits hidden). Tick confirmation below.'
      }));
      return;
    }

    // Additional validation for image files
    if (file.type.startsWith('image/')) {
      // Check image dimensions (optional)
      const img = new Image();
      img.onload = () => {
        if (img.width < 100 || img.height < 100) {
          setErrors(prev => ({
            ...prev,
            [docType]: 'Image resolution too low. Please upload a higher quality image.'
          }));
          return;
        }
        startUpload(docType, file);
      };
      img.onerror = () => {
        setErrors(prev => ({
          ...prev,
          [docType]: 'Invalid image file. Please try again.'
        }));
      };
      img.src = URL.createObjectURL(file);
    } else {
      startUpload(docType, file);
    }
  };

  const startUpload = (docType, file) => {
    // Simulate upload progress
    setUploadProgress(prev => ({ ...prev, [docType]: 0 }));
    
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        const current = prev[docType] || 0;
        if (current >= 100) {
          clearInterval(progressInterval);
          return prev;
        }
        return { ...prev, [docType]: current + Math.random() * 15 + 5 };
      });
    }, 150);

    // Simulate upload completion
    setTimeout(() => {
      setDocuments(prev => ({
        ...prev,
        [docType]: {
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          url: URL.createObjectURL(file),
          uploadedAt: new Date().toISOString(),
          status: 'uploaded'
        }
      }));
      setUploadProgress(prev => ({ ...prev, [docType]: 100 }));
    }, 1500 + Math.random() * 1000);
  };

  const handleFileInputChange = (docType, event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileSelect(docType, file);
    }
  };

  const onDragOver = (docType, e) => {
    e.preventDefault();
    setDragOver(prev => ({ ...prev, [docType]: true }));
  };

  const onDragLeave = (docType, e) => {
    e.preventDefault();
    setDragOver(prev => ({ ...prev, [docType]: false }));
  };

  const onDrop = (docType, e) => {
    e.preventDefault();
    setDragOver(prev => ({ ...prev, [docType]: false }));
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileSelect(docType, file);
  };

  const removeDocument = (docType) => {
    setDocuments(prev => ({
      ...prev,
      [docType]: null
    }));
    setUploadProgress(prev => ({
      ...prev,
      [docType]: 0
    }));
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[docType];
      return newErrors;
    });
  };

  const openFileDialog = (docType) => {
    fileInputRefs.current[docType]?.click();
  };

  const previewDocument = (docType) => {
    const doc = documents[docType];
    if (doc && doc.url) {
      window.open(doc.url, '_blank');
    }
  };

  const validateDocuments = () => {
    const newErrors = {};
    let hasErrors = false;

    Object.keys(documentTypes).forEach(docType => {
      if (documentTypes[docType].required && !documents[docType]) {
        newErrors[docType] = `${documentTypes[docType].label} is required`;
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSubmit = async () => {
    if (!validateDocuments()) {
      return;
    }

    setIsUploading(true);

    try {
      if (vendorId) {
        // Create FormData for file upload
        const formData = new FormData();
        
        Object.keys(documents).forEach(docType => {
          const doc = documents[docType];
          if (doc && doc.file) {
            formData.append(docType, doc.file);
          }
        });

        const response = await uploadClient.post(endpoints.kycUpload(vendorId), formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.status === 200) {
          const kycData = {
            documents,
            submittedAt: new Date().toISOString(),
            status: response.data.status
          };
          try { localStorage.setItem('kycSubmitted', '1'); } catch (e) {}
          try { window.dispatchEvent(new CustomEvent('navigate', { detail: { tab: 'dashboard' } })); } catch (e) {}
          onKYCComplete(kycData);
        }
      } else {
        // Fallback for demo mode
        const kycData = {
          documents,
          submittedAt: new Date().toISOString(),
          status: 'SUBMITTED'
        };
        try { localStorage.setItem('kycSubmitted', '1'); } catch (e) {}
        try { window.dispatchEvent(new CustomEvent('navigate', { detail: { tab: 'dashboard' } })); } catch (e) {}
        onKYCComplete(kycData);
      }
    } catch (error) {
      console.error('KYC upload error:', error);
      setErrors({ general: 'Failed to upload documents. Please try again.' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) {
      return <LucideIcons.Image size={20} />;
    }
    return <LucideIcons.FileText size={20} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="kyc-upload-container">
      <div className="kyc-upload-card">
        <div className="kyc-upload-header">
          <div className="kyc-upload-logo">
            <LucideIcons.FileText size={24} />
            <h2>KYC Document Upload</h2>
          </div>
          <p className="kyc-upload-subtitle">
            Upload your business documents for verification
          </p>
        </div>

        <div className="kyc-upload-content">
          <div className="upload-summary">
            <div className="summary-stats">
              <div className="stat-item">
                <span className="stat-number">{Object.keys(documents).filter(key => documents[key]).length}</span>
                <span className="stat-label">Uploaded</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{Object.keys(documentTypes).length}</span>
                <span className="stat-label">Total Required</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {Math.round((Object.keys(documents).filter(key => documents[key]).length / Object.keys(documentTypes).length) * 100)}%
                </span>
                <span className="stat-label">Complete</span>
              </div>
            </div>
            <div className="overall-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ 
                    width: `${(Object.keys(documents).filter(key => documents[key]).length / Object.keys(documentTypes).length) * 100}%` 
                  }}
                />
              </div>
            </div>
          </div>
          
          <div className="documents-grid">
            {Object.keys(documentTypes).map(docType => {
              const doc = documents[docType];
              const progress = uploadProgress[docType] || 0;
              const isUploading = progress > 0 && progress < 100;
              const isComplete = progress === 100;

              return (
                <div key={docType} className="document-card">
                  <div className="document-header">
                    <div className="document-info">
                      {getFileIcon(doc?.type)}
                      <div>
                        <h4>{documentTypes[docType].label}</h4>
                        <p className="document-description">{documentTypes[docType].description}</p>
                        {documentTypes[docType].required && (
                          <span className="required-badge">Required</span>
                        )}
                      </div>
                    </div>
                    {isComplete && (
                      <LucideIcons.CheckCircle size={20} className="success-icon" />
                    )}
                  </div>

                  {!doc ? (
                    <div 
                      className={`upload-area ${dragOver[docType] ? 'drag-over' : ''}`}
                      onDragOver={(e) => onDragOver(docType, e)}
                      onDragLeave={(e) => onDragLeave(docType, e)}
                      onDrop={(e) => onDrop(docType, e)}
                    >
                      <input
                        type="file"
                        ref={el => fileInputRefs.current[docType] = el}
                        onChange={(e) => handleFileInputChange(docType, e)}
                        accept=".jpg,.jpeg,.png,.pdf"
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        className="upload-btn"
                        onClick={() => openFileDialog(docType)}
                        disabled={isUploading}
                      >
                        <LucideIcons.Upload size={20} />
                        Choose File
                      </button>
                      <p className="upload-hint">
                        Max {documentTypes[docType].maxSize}MB • {documentTypes[docType].acceptedFormats}
                      </p>
                      {docType === 'aadhaar' && (
                        <label className="aadhaar-mask-check">
                          <input type="checkbox" checked={aadhaarMaskedConfirmed} onChange={(e) => setAadhaarMaskedConfirmed(e.target.checked)} />
                          I confirm my Aadhaar is masked (first 8 digits hidden)
                        </label>
                      )}
                    </div>
                  ) : (
                    <div className="document-preview">
                      <div className="document-details">
                        <div className="document-name">
                          {getFileIcon(doc.type)}
                          <span>{doc.name}</span>
                        </div>
                        <div className="document-meta">
                          <span>{formatFileSize(doc.size)}</span>
                          <span>•</span>
                          <span>{new Date(doc.uploadedAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="document-actions">
                        <button
                          type="button"
                          className="preview-btn"
                          onClick={() => previewDocument(docType)}
                        >
                          <LucideIcons.Eye size={16} />
                        </button>
                        <button
                          type="button"
                          className="remove-btn"
                          onClick={() => removeDocument(docType)}
                        >
                          <LucideIcons.X size={16} />
                        </button>
                      </div>
                    </div>
                  )}

                  {isUploading && (
                    <div className="upload-progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className="progress-text">{progress}%</span>
                    </div>
                  )}

                  {errors[docType] && (
                    <div className="error-message">
                      <LucideIcons.AlertCircle size={14} />
                      <span>{errors[docType]}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="kyc-upload-actions">
          <button
            type="button"
            className="skip-btn"
            onClick={handleSkip}
            disabled={isUploading}
          >
            Skip for Now
          </button>
          <button
            type="button"
            className="submit-btn"
            onClick={handleSubmit}
            disabled={isUploading}
          >
            {isUploading ? (
              <>
                <div className="spinner"></div>
                Submitting...
              </>
            ) : (
              'Submit Documents'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KYCUpload;
